import {
	baseApiHeaders,
	HIDE_TOAST_KEY,
	type IApiResponse,
	type IDeveloperIntegration,
	type IDeveloperIntegrationAPISecret,
	type IDeveloperIntegrationWebhookAPISecret,
	type IWebhookLogsProps,
	type ParamType,
} from 'BreetHelpers';

import { apiSlice } from './apiSlice';

export const developerApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		setWebhookUrl: builder.mutation<IApiResponse, { webhookUrl: string }>({
			query: (body) => ({
				method: 'POST',
				url: '/users/set-webhook-url',
				body,
				headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' },
			}),
			invalidatesTags: ['GetDeveloperIntegration'],
		}),
		generateDeveloperIntegrationAPISecret: builder.mutation<IApiResponse<IDeveloperIntegrationAPISecret>, void>({
			query: () => ({
				method: 'POST',
				url: '/users/generate-secret',
			}),
			invalidatesTags: ['GetDeveloperIntegration'],
		}),
		generateDeveloperIntegrationWebhookAPISecret: builder.mutation<IApiResponse<IDeveloperIntegrationWebhookAPISecret>, void>({
			query: () => ({
				method: 'POST',
				url: '/users/generate-webhook-secret',
			}),
			invalidatesTags: ['GetDeveloperIntegration'],
		}),
		setIpAddress: builder.mutation<IApiResponse, { ipAddresses: string[] }>({
			query: (body) => ({
				method: 'PUT',
				url: '/users/ip-addresses',
				body,
			}),
			invalidatesTags: ['GetDeveloperIntegration'],
		}),
		updateMarkupPercentage: builder.mutation<IApiResponse, { markupPercent: number }>({
			query: (body) => ({
				method: 'PUT',
				url: '/users/markup-percent',
				body,
			}),
			invalidatesTags: ['GetDeveloperIntegration'],
		}),
		getDeveloperIntegration: builder.query<IApiResponse<IDeveloperIntegration>, void>({
			query: () => ({ url: '/users/fetch-integration', headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' } }),
			providesTags: ['GetDeveloperIntegration'],
		}),
		getWebhookLogs: builder.query<IApiResponse<IWebhookLogsProps[]>, ParamType>({
			query: (params) => ({ url: '/transactions/webhooks', params, headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' } }),
			providesTags: ['GetWebhookLogs'],
		}),
		getWebhookLogById: builder.query<IApiResponse<IWebhookLogsProps>, string>({
			query: (id) => ({ url: `/transactions/webhooks/${id}`, headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' } }),
			providesTags: ['GetWebhookLogById'],
		}),
		resendWebhookByReference: builder.mutation<IApiResponse, string>({
			query: (reference) => ({
				method: 'POST',
				url: `/transactions/webhooks/resend/${reference}`,
			}),
			invalidatesTags: ['GetWebhookLogs', 'GetWebhookLogById'],
		}),
		automateTradeResolution: builder.mutation<IApiResponse, boolean>({
			query: (isEnable) => ({
				method: 'PUT',
				url: `/users/auto-resolution/${isEnable ? 'disable' : 'enable'}`,
			}),
			invalidatesTags: ['GetDeveloperIntegration'],
		}),
	}),
});

export const {
	useSetWebhookUrlMutation,
	useGetDeveloperIntegrationQuery,
	useGenerateDeveloperIntegrationAPISecretMutation,
	useGenerateDeveloperIntegrationWebhookAPISecretMutation,
	useSetIpAddressMutation,
	useUpdateMarkupPercentageMutation,
	useGetWebhookLogsQuery,
	useGetWebhookLogByIdQuery,
	useResendWebhookByReferenceMutation,
	useAutomateTradeResolutionMutation,
} = developerApiSlice;

export const selectDeveloperIntegration = developerApiSlice.endpoints.getDeveloperIntegration.select();
